/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosError, AxiosResponse } from "axios";

import { ENDPOINT } from "@/config";
export default class RestAPIClient<T> {
  private client = axios.create({
    baseURL: ENDPOINT,
  });
  private controller: string;
  private responseBody = <T>(response: AxiosResponse<T>) => response.data;
  constructor(_controller: string) {
    this.controller = _controller;

    this.client.interceptors.request.use((config) => {
      let user = localStorage.getItem("user") as any;

      if (user && !config.url?.endsWith("sign-up")) {
        user = JSON.parse(user) as any;
        const access_token = user.access_token;
        if (access_token && config.headers) {
          config.headers.Authorization = `mytoken ${access_token}`;
        }
      }

      return config;
    });

    this.client.interceptors.response.use(
      async (response) => {
        return response;
      },
      (error: AxiosError) => {
        if (error?.request && error?.response) {
          const { status } = error.response as AxiosResponse;
          let errorMessage: string;
          switch (status) {
            case 400:
              errorMessage =
                "Invalid request. Please check your input and try again.";
              break;
            case 401:
              errorMessage = "You are not authorized to access this resource.";
              break;
            case 404:
              errorMessage =
                "The requested resource could not be found. Please try again later.";
              break;
            case 500:
              errorMessage = "Something went wrong. Please try again later.";
              break;
            default:
              errorMessage =
                "Unexpected error. Please try again later or contact the administrator.";
              console.error(error);
          }
          return Promise.reject({
            code: status,
            message: errorMessage,
            trace: error,
            raw: error.response?.data,
          });
        } else {
          console.error(error);
          return Promise.reject({
            code: error.response?.status,
            message:
              "Unexpected error. Please try again later or contact the administrator.",
            trace: error,
            raw: error.response?.data,
          });
        }
      },
    );
  }
  public healthCheck(): Promise<string> {
    return this.client.get("/healthcheck").then(this.responseBody);
  }
  getAll(method = ""): Promise<Array<T>> {
    return this.client
      .get(`${this.controller}/${method}`)
      .then(this.responseBody);
  }
  get(param: { method?: string; id: string }): Promise<T> {
    if (!param.method) {
      param.method = "";
    }
    return this.client
      .get(
        `${this.controller}/${param.method ? param.method : ""}${
          param.id ? "/" + param.id : ""
        }`,
      )
      .then(this.responseBody);
  }
  post = async (param: { method?: string; request: any; headers?: any }) => {
    if (!param.method) {
      param.method = "";
    }
    return await this.client
      .post(`${this.controller}/${param.method}`, param.request, {
        headers: param.headers,
      })
      .then(this.responseBody);
  };
  create(param: { method?: string; request: any; headers?: any }): Promise<T> {
    if (!param.method) {
      param.method = "";
    }
    return this.client
      .post(`${this.controller}/${param.method}`, param.request, {
        headers: param.headers,
      })
      .then(this.responseBody);
  }
  update(param: { method?: string; id: string; request: any }): Promise<T> {
    if (!param.method) {
      param.method = "";
    }
    return this.client
      .put(`${this.controller}/${param.method}/${param.id}`, param.request)
      .then(this.responseBody);
  }
  delete(param: { method?: string; id: string }): Promise<void> {
    if (!param.method) {
      param.method = "delete";
    }
    return new Promise((resolve, reject) => {
      this.client
        .delete(`${this.controller}/${param.method}/${param.id}`)
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  }
  blobToBase64(blob: any, callback: (base64: string) => void) {
    const reader = new FileReader();
    reader.onload = function () {
      const dataUrl = reader.result;

      const base64 = (dataUrl as string).split(",")[1];
      callback(base64);
    };
    reader.readAsDataURL(blob);
  }
  toArrayBuffer(buf: any) {
    const ab = new ArrayBuffer(buf.length);
    const view = new Uint8Array(ab);
    for (let i = 0; i < buf.length; ++i) {
      view[i] = buf[i];
    }
    return ab;
  }
  download(method: string, request: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`${this.controller}${method}`, request, {
          responseType: "arraybuffer",
        })
        .then((response) => {
          resolve(response.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }
  auth(
    method: string,
    param: { username: string; password: string },
  ): Promise<T> {
    return this.client
      .post(`${this.controller}/${method}`, param)
      .then(this.responseBody);
  }
}
